import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ContentTemplate, ContentTemplatesCollection, ContentTemplateText } from '../models/content-templates';
import { RestUtils } from './rest-utils';

@Injectable({
  providedIn: 'root',
})
export class ContentTemplateService {
  http: HttpClient;

  utils = new RestUtils();

  headers = new HttpHeaders();

  constructor(http: HttpClient) {
    this.http = http;
    this.headers = this.headers.set('Content-Type', 'application/json');
  }

  all(params: AllRequestParams): Observable<ContentTemplatesCollection> {
    const queryParams = {
      page: params.page,
      size: params.size,
      searchString: params.search ? params.search : '',
      sort: params.sort && params.sort.length ? params.sort : [],
      withText: params.withText,
      searchSenderOnly: params.searchSenderOnly,
    };
    const url = this.utils.buildUrl(`ROLE/ntc/sms-templates`, queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<ContentTemplatesCollection>(url, options);
  }

  one(id: number): Observable<ContentTemplate> {
    const url = this.utils.buildUrl(`ROLE/ntc/sms-templates/${id}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<ContentTemplate>(url, options);
  }

  createUpdateSmsTemplates(data): Observable<any> {
    const url = this.utils.buildUrl(`ROLE/ntc/sms-templates`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    if (data.id) {
      return this.http.put(url, data, options);
    }
    return this.http.post(url, data, options);
  }

  updateVisibleAllToggle(data): Observable<any> {
    const url = this.utils.buildUrl(`ROLE/ntc/sms-templates`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.patch(url, data, options);
  }

  updateDefaultToggle(smsTemplateTextId: number): Observable<any> {
    const url = this.utils.buildUrl(`ROLE/ntc/sms-templates/text/default/${smsTemplateTextId}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post(url, {}, options);
  }

  updateGlobalDefaultAsAdminToggle(smsTemplateTextId: number): Observable<any> {
    const url = this.utils.buildUrl(`ROLE/ntc/sms-templates/text/default/${smsTemplateTextId}?globalDefault=true`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post(url, {}, options);
  }

  updateFavorite(smsTextTemplateId: number, isFavorite: boolean): Observable<ContentTemplate[]> {
    const url = this.utils.buildUrl(`ROLE/ntc/sms-templates/text/favourite/${smsTextTemplateId}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    if (isFavorite) {
      return this.http.post<any>(url, {}, options);
    }
    return this.http.delete<ContentTemplate[]>(url, options);
  }

  delete(id: number): Observable<any> {
    const url = this.utils.buildUrl(`ROLE/ntc/sms-templates/${id}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.delete(url, options);
  }

  deleteTextTemplate(textTemplateId: number): Observable<any> {
    const url = this.utils.buildUrl(`ROLE/ntc/sms-templates/text/${textTemplateId}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.delete<any>(url, options);
  }

  getFavourites(): Observable<ContentTemplate[]> {
    const url = this.utils.buildUrl(`ROLE/ntc/sms-templates/favourites`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<ContentTemplate[]>(url, options);
  }

  create(): ContentTemplate {
    return {
      id: null,
      userId: null,
      visibleAll: false,
      name: '',
      description: '',
      smsTemplateTextDtos: [],
    };
  }

  formatStructrue(contentTemplates: ContentTemplate[], withFavourites = false): any[] {
    return contentTemplates.map(ct => {
      const currentTextTemplates = ct.smsTemplateTextDtos.map(stt => ({
        id: stt.id,
        content: stt.value,
        description: stt.description,
        favorite: stt.userFavourite,
        telqIdCase: stt.telqIdCase,
        telqIdLength: stt.telqIdLength,
        telqIdType: stt.telqIdType,
      }));
      if (withFavourites) {
        return {
          id: ct.id,
          senderId: ct.name,
          textTemplates: currentTextTemplates,
          nonFavTextTemplates: [],
          favTextTemplates: currentTextTemplates,
        };
      }
      return {
        id: ct.id,
        senderId: ct.name,
        textTemplates: currentTextTemplates,
      };
    });
  }

  getImportExampleFileUrl() {
    return window.location.origin + '/templates/content_template.xlsx';
  }

  import(data: ContentTemplate[]): Observable<any> {
    const url = this.utils.buildUrl(`ROLE/ntc/sms-templates/import`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post(url, data, options);
  }

  static generateTextId(contentTemplate: ContentTemplate, text: ContentTemplateText): string {
    const parts = [contentTemplate.name, text.value, text.telqIdType];
    if (text.telqIdType === 'ALPHA' || text.telqIdType === 'ALPHA_NUMERIC') {
      parts.push(text.telqIdCase);
    }
    if (text.telqIdType !== 'WHATSAPP_CODE') {
      parts.push(String(text.telqIdLength));
    }
    return parts.join('_');
  }
}

export class AllRequestParams {
  size = 20;
  page = 1;
  search: string;
  withText = false;
  searchSenderOnly = false;

  sort: string[] = [];

  setSort(propertyName: string, direction: string) {
    this.sort.push(propertyName + (direction === 'desc' ? ',desc' : ''));
  }

  removeSort(propertyName: string) {
    this.sort = this.sort.filter(_ => _.indexOf(propertyName) === -1);
  }

  resetSort() {
    this.sort = [];
  }
}
